"use client";

import Link from 'next/link';
import { Download, ExternalLink } from 'lucide-react';

import { useAppAvailability } from '@/hooks/use-app-availability';
import { apkUrl } from '@/lib/appDownload';

type Variant = 'hero' | 'solid' | 'link';

type Props = {
  variant?: Variant;
  className?: string;
  label?: string;
};

// `hero` matches the circular actions on the landing page; `solid` is the wide
// call to action on the download page; `link` is the quiet footer entry. The
// first two always route through /download/ so the requirements and the safety
// note are read before anything is installed.
export default function DownloadAppButton({
  variant = 'solid',
  className = '',
  label,
}: Props) {
  const availability = useAppAvailability();

  if (availability.route === 'loading' || availability.route === 'none') {
    return null;
  }

  const onPlay = availability.route === 'play';
  const text = label ?? (onPlay ? 'Get it on Google Play' : 'Download App');
  const shared = 'transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2';

  if (variant === 'link') {
    return (
      <Link
        href="/download/"
        className={`inline-flex items-center gap-2 text-gray-400 hover:text-white ${shared} ${className}`}
      >
        <Download className="h-3.5 w-3.5" />
        <span>{text}</span>
      </Link>
    );
  }

  if (variant === 'hero') {
    return (
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        <Link
          href="/download/"
          aria-label={text}
          className={`
            flex h-16 w-16 items-center justify-center rounded-full
            bg-white text-indigo-500 sm:h-18 sm:w-18
            shadow-[0_15px_35px_-10px_rgba(99,102,241,0.4)]
            ease-[cubic-bezier(0.34,1.56,0.64,1)]
            hover:-translate-y-2 hover:scale-105
            hover:shadow-[0_20px_40px_-10px_rgba(99,102,241,0.6)]
            active:translate-y-1 active:scale-95
            ${shared}
          `}
        >
          <Download className="h-7 w-7" />
        </Link>
        <span className="text-sm font-semibold text-slate-600 sm:text-base">
          {text}
        </span>
      </div>
    );
  }

  const solid = `
    inline-flex items-center justify-center gap-3 rounded-full
    bg-gradient-to-br from-indigo-500 to-violet-500 px-8 py-4
    text-base font-semibold text-white
    shadow-[0_15px_35px_-10px_rgba(99,102,241,0.5)]
    hover:-translate-y-1 hover:shadow-[0_20px_40px_-10px_rgba(99,102,241,0.7)]
    active:translate-y-0.5
    ${shared} ${className}
  `;

  if (onPlay) {
    return (
      <a
        href={availability.url}
        target="_blank"
        rel="noopener noreferrer"
        className={solid}
      >
        <ExternalLink className="h-5 w-5" />
        {text}
      </a>
    );
  }

  // The direct file link: `download` asks the browser to save rather than
  // navigate, which matters because some Android browsers preview an APK.
  return (
    <a href={apkUrl} download className={solid}>
      <Download className="h-5 w-5" />
      {text}
    </a>
  );
}
